import { useEffect } from 'react'
import { Box, Input, Title } from '@holbonkit/ui'
import { Controller, useForm } from 'react-hook-form'

import { MaskedInput } from '@/shared/ui'

import { capitalizeFirstLetter } from '../../lib/capitalize-first-letter'
import type { TStepFormProps } from '../../lib/step-form-props'

import type { TPersonalDataForm } from './types'
import { personalDataValidator } from './validator'

import styles from './personal-data.module.scss'

export const PERSONAL_DATA_FORM_ID = 'smz-personal-data-form'

const EMPTY_PERSONAL_DATA: TPersonalDataForm = {
    birthDate: '',
    email: '',
    name: '',
    patronymic: '',
    surname: '',
}

type TPersonalDataProps = TStepFormProps<TPersonalDataForm>

export const PersonalData = ({
    defaultValues,
    formId,
    onSubmit,
    onValidityChange,
}: TPersonalDataProps) => {
    const {
        clearErrors,
        control,
        handleSubmit,
        reset,
        formState: { isValid },
    } = useForm<TPersonalDataForm>({
        defaultValues: EMPTY_PERSONAL_DATA,
        mode: 'onTouched',
        reValidateMode: 'onChange',
    })

    useEffect(() => {
        reset(defaultValues ?? EMPTY_PERSONAL_DATA)
    }, [defaultValues, reset])

    useEffect(() => {
        onValidityChange?.(isValid)
    }, [isValid, onValidityChange])

    return (
        <form noValidate id={formId} onSubmit={handleSubmit(onSubmit)}>
            <Box className={styles['personal-data']} pt={6} px={4}>
                <Title variant='header-24-bold'>Личные данные</Title>
                <Controller
                    control={control}
                    name='surname'
                    rules={personalDataValidator.surname}
                    render={({ field, fieldState }) => (
                        <Input
                            errorText={fieldState.error?.message}
                            inputMode='text'
                            label='Фамилия'
                            size='lg'
                            {...field}
                            maxLength={50}
                            onChange={(event) => {
                                field.onChange(capitalizeFirstLetter(event.target.value))
                            }}
                            onFocus={() => {
                                clearErrors('surname')
                            }}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name='name'
                    rules={personalDataValidator.name}
                    render={({ field, fieldState }) => (
                        <Input
                            errorText={fieldState.error?.message}
                            inputMode='text'
                            label='Имя'
                            size='lg'
                            {...field}
                            maxLength={50}
                            onChange={(event) => {
                                field.onChange(capitalizeFirstLetter(event.target.value))
                            }}
                            onFocus={() => {
                                clearErrors('name')
                            }}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name='patronymic'
                    rules={personalDataValidator.patronymic}
                    render={({ field, fieldState }) => (
                        <Input
                            errorText={fieldState.error?.message}
                            inputMode='text'
                            label='Отчество'
                            size='lg'
                            {...field}
                            maxLength={50}
                            onChange={(event) => {
                                field.onChange(capitalizeFirstLetter(event.target.value))
                            }}
                            onFocus={() => {
                                clearErrors('patronymic')
                            }}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name='birthDate'
                    rules={personalDataValidator.birthDate}
                    render={({ field, fieldState }) => (
                        <MaskedInput
                            data-test-id='personal-data__birth-date'
                            errorText={fieldState.error?.message}
                            label='Дата рождения'
                            name={field.name}
                            preset='date'
                            value={field.value}
                            onBlur={field.onBlur}
                            onChange={field.onChange}
                            onFocus={() => {
                                clearErrors('birthDate')
                            }}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name='email'
                    rules={personalDataValidator.email}
                    render={({ field, fieldState }) => (
                        <Input
                            errorText={fieldState.error?.message}
                            inputMode='email'
                            label='Электронная почта'
                            size='lg'
                            type='text'
                            {...field}
                            onChange={(event) => {
                                field.onChange(event.target.value.trim().toLowerCase())
                            }}
                            onFocus={() => {
                                clearErrors('email')
                            }}
                        />
                    )}
                />
            </Box>
        </form>
    )
}
